import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cities as citiesApi } from "../../services/api";
import { ArrowLeft, Loader, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";

const CATEGORY_COLORS = {
  food: "#f59e0b",
  stay: "#8b5cf6",
  adventure: "#06d6a0",
};

function CitiesIndex() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Explore Cities — Pinned`;
    return () => { document.title = t("app.title"); };
  }, [t]);

  useEffect(() => {
    async function load() {
      try {
        const data = await citiesApi.getAll();
        setCities(data);
      } catch {
        setCities([]);
      }
      setLoading(false);
    }
    load();
  }, []);

  return (
    <div className="h-full flex flex-col bg-th-bg">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 pt-[max(1rem,env(safe-area-inset-top))] mt-2 pb-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 bg-th-surface border border-th-border text-th-muted rounded-xl flex items-center justify-center"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-th-text">{t("cities.title")}</h1>
              <p className="text-xs text-th-muted">{t("cities.subtitle")}</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader size={20} className="animate-spin text-th-accent" />
            </div>
          ) : cities.length === 0 ? (
            <p className="text-center text-sm text-th-muted py-12">{t("cities.empty")}</p>
          ) : (
            <div className="space-y-2">
              {cities.map((city) => (
                <Link
                  key={`${city.city}-${city.countryCode}`}
                  to={`/explore/${encodeURIComponent(city.city)}`}
                  className="flex items-center gap-3 px-4 py-3 bg-th-card border border-th-border rounded-xl"
                >
                  <div className="w-9 h-9 rounded-lg bg-th-accent-dim border border-th-accent-border flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-th-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-th-text">{city.city}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-th-muted font-mono uppercase">{city.countryCode}</span>
                      <span className="text-[11px] text-th-muted font-mono">
                        {city.postCount} {city.postCount === 1 ? "pin" : "pins"}
                      </span>
                      <div className="flex gap-1">
                        {city.categories.map((c) => (
                          <span
                            key={c}
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: CATEGORY_COLORS[c] }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-th-accent font-mono">{city.postCount}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CitiesIndex;
